import { ChatMessage } from "@/lib/ai/types";
import { jsonParseOrUndefined } from "@/util/json-parse-or-undefined";
import { jsonStringifyOrUndefined } from "@/util/json-stringify-or-undefined";
import { fetchWithJWT } from "@/futurelayer/auth/jwt";

export type BatchRequest = {
  id: string;
  messages: ChatMessage[];
  responseFormat?: any;
};

export type BatchResponse = {
  id: string;
  content: string | null;
  error?: string;
}

export type BatchOptions = {
  baseUrl?: string;
  licenseKey?: string;
  
}

export async function callAIBatch(
  requests: BatchRequest[],
  abortController: AbortController,
  onResponse?: (response: BatchResponse) => void,
): Promise<BatchResponse[]> {

  try {
    const response = await fetchWithJWT("/ai/batch", {
      body: jsonStringifyOrUndefined({ requests }),
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error("Failed to get response reader");
    }

    const decoder = new TextDecoder();
    const results: BatchResponse[] = [];

    const trickEsLint = true;

    while (trickEsLint) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value);
      const lines = chunk.split("\n").filter(line => line.trim());

      for (const line of lines) {
        try {
          const result: BatchResponse | undefined = jsonParseOrUndefined(line);

          if (result === undefined) {
            console.warn("Received undefined response for line:", line);
            continue;
          }

          results.push(result);

          if (onResponse) {
            onResponse(result);
          }
        } catch (parseError) {
          console.error("Failed to parse JSON:", line);
        }
      }
    }

    return results;
  } catch (error) {
    console.error("Batch AI request failed:", error);
    throw error;
  }
}

export async function callRequestStructuredOutputFromRailway(
  messages: ChatMessage[],
  json_schema: any,
  abortController: AbortController
): Promise<{ content: string; usage?: any } | null> {
  try {
    const response = await fetchWithJWT("/ai/structured-output", {
      body: jsonStringifyOrUndefined({
        messages,
        json_schema
      }),
      signal: abortController.signal
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Structured output request failed:", error);
    throw error;
  }
}

